import React, { useState } from 'react';
import {
  Container,
  Paper,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
  MenuItem,
  Stack,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import { useNavigate, Navigate } from 'react-router';
import TextField from '../components/TextField';
import Picklist from '../components/Picklist';
import { useAuth } from '../context/AuthContext';

const CAREGIVER_TYPES = ['HHA', 'CNA', 'RN', 'LPN', 'OT', 'OTA', 'PT', 'PTA', 'SLP'];

function getQualificationQuestionKeys(type: string): string[] {
  switch (type) {
    case 'RN':
      return [
        'register.qualQuestion1RN',
        'register.qualQuestion2RN',
        'register.qualQuestion3RN',
        'register.qualQuestion4RN',
        'register.qualQuestion5RN',
      ];
    case 'LPN':
      return [
        'register.qualQuestion1LPN',
        'register.qualQuestion2RN',
        'register.qualQuestion3RN',
        'register.qualQuestion4RN',
        'register.qualQuestion5RN',
      ];
    case 'PT':
      return [
        'register.qualQuestion1PT',
        'register.qualQuestion2PT',
        'register.qualQuestion3PT',
        'register.qualQuestion4PT',
        'register.qualQuestion5PT',
      ];
    case 'PTA':
      return [
        'register.qualQuestion1PTA',
        'register.qualQuestion2PT',
        'register.qualQuestion3PT',
        'register.qualQuestion4PT',
        'register.qualQuestion5PTA',
      ];
    case 'OT':
      return [
        'register.qualQuestion1OT',
        'register.qualQuestion2PT',
        'register.qualQuestion3PT',
        'register.qualQuestion4PT',
        'register.qualQuestion5OT',
      ];
    case 'OTA':
      return [
        'register.qualQuestion1OTA',
        'register.qualQuestion2PT',
        'register.qualQuestion3PT',
        'register.qualQuestion4PT',
        'register.qualQuestion5OTA',
      ];
    case 'SLP':
      return [
        'register.qualQuestion1SLP',
        'register.qualQuestion2PT',
        'register.qualQuestion3PT',
        'register.qualQuestion4PT',
        'register.qualQuestion5SLP',
      ];
    default:
      return [
        'register.qualQuestion1',
        'register.qualQuestion2',
        'register.qualQuestion3',
        'register.qualQuestion4',
        'register.qualQuestion5',
      ];
  }
}

type Phase = 'info' | 'qualification' | 'disqualified';

const RegistrationForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { registrationData, setRegistrationData, login } = useAuth();

  const prefill = registrationData?.nexusData;

  const [phase, setPhase] = useState<Phase>('info');
  const [firstName, setFirstName] = useState(prefill?.firstName || '');
  const [lastName, setLastName] = useState(prefill?.lastName || '');
  const [email, setEmail] = useState(prefill?.email || '');
  const [countyName, setCountyName] = useState(prefill?.countyName || '');
  const [type, setType] = useState(prefill?.type || '');
  const [nexusId, setNexusId] = useState('');
  const [answers, setAnswers] = useState<Record<string, 'Yes' | 'No'>>({
    q1: 'No', q2: 'No', q3: 'No', q4: 'No', q5: 'No',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!registrationData) {
    return <Navigate to="/start" replace />;
  }

  const handleInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!firstName.trim() || !lastName.trim() || !email.trim() || !type) {
      setError(t('form.requiredFieldsMissing', 'Please fill in all required fields.'));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          registrationToken: registrationData.registrationToken,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          type,
          countyName: countyName || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Registration failed');
        return;
      }

      setNexusId(data.nexusId);

      if (!data.needsQualification) {
        // Already past lead status -- skip qualification, create user directly
        const qualifyResponse = await fetch('/api/auth/qualify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            registrationToken: registrationData.registrationToken,
            nexusId: data.nexusId,
            qualificationAnswers: { q1: 'Yes', q2: 'Yes', q3: 'Yes', q4: 'Yes', q5: 'Yes' },
          }),
        });
        const qualifyData = await qualifyResponse.json();
        if (qualifyData.qualified) {
          setRegistrationData(null);
          login(qualifyData.accessToken, qualifyData.refreshToken, qualifyData.user);
          navigate('/caregiverform');
          return;
        }
      }

      setPhase('qualification');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleQualificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch('/api/auth/qualify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          registrationToken: registrationData.registrationToken,
          nexusId,
          qualificationAnswers: answers,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Qualification failed');
        return;
      }

      if (!data.qualified) {
        setPhase('disqualified');
        return;
      }

      setRegistrationData(null);
      login(data.accessToken, data.refreshToken, data.user);
      navigate('/caregiverform');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const questionKeys = getQualificationQuestionKeys(type);

  return (
    <Container maxWidth="sm" sx={{ mt: 5 }}>
      <Paper elevation={3} sx={{ p: 3, borderRadius: '10px' }}>
        <Box textAlign="center" mb={3}>
          <img
            src="/images/logo/evo-logo.png"
            alt="Logo"
            style={{ maxWidth: '50%', height: 'auto', marginBottom: '10px' }}
          />
        </Box>

        {phase === 'info' && (
          <>
            <Typography variant="h6" fontWeight={600} textAlign="center" mb={2}>
              {t('register.signUpButton', 'Sign Up')}
            </Typography>
            <form onSubmit={handleInfoSubmit}>
              <Grid2 container spacing={2}>
                <Grid2 size={12}>
                  <TextField
                    label={t('form.firstName', 'First Name')}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    fullWidth
                    required
                  />
                </Grid2>
                <Grid2 size={12}>
                  <TextField
                    label={t('form.lastName', 'Last Name')}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    fullWidth
                    required
                  />
                </Grid2>
                <Grid2 size={12}>
                  <TextField
                    label={t('form.email', 'Email')}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    required
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Picklist
                    fieldName="countyName"
                    value={countyName}
                    onChange={(e) => setCountyName(e.target.value)}
                    label={t('form.countyName', 'County')}
                    objectName="caregiver"
                  />
                </Grid2>
                <Grid2 size={12}>
                  <TextField
                    label={t('register.caregiverTypeLabel', 'Caregiver Type')}
                    fullWidth
                    select
                    required
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  >
                    {CAREGIVER_TYPES.map((ct) => (
                      <MenuItem key={ct} value={ct}>
                        {ct}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid2>
              </Grid2>
              <Box mt={3}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={loading}
                  sx={{ p: 1.5 }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : t('form.next', 'Next')}
                </Button>
              </Box>
            </form>
          </>
        )}

        {phase === 'qualification' && (
          <form onSubmit={handleQualificationSubmit}>
            {questionKeys.map((key, idx) => {
              const qKey = `q${idx + 1}` as keyof typeof answers;
              return (
                <Paper key={key} elevation={2} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {t(key)}
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant={answers[qKey] === 'Yes' ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => setAnswers((prev) => ({ ...prev, [qKey]: 'Yes' }))}
                    >
                      {t('common.yes', 'Yes')}
                    </Button>
                    <Button
                      variant={answers[qKey] === 'No' ? 'contained' : 'outlined'}
                      color="error"
                      onClick={() => setAnswers((prev) => ({ ...prev, [qKey]: 'No' }))}
                    >
                      {t('common.no', 'No')}
                    </Button>
                  </Stack>
                </Paper>
              );
            })}
            <Box mt={2}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading}
                sx={{ p: 1.5 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : t('register.submitAnswers', 'Submit Answers')}
              </Button>
            </Box>
          </form>
        )}

        {phase === 'disqualified' && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t('register.qualificationFailedMessage', "Sorry! Based on your answers, it doesn't seem like you qualify. Please reach out to our onboarding department if you have any questions (305) 228-4800 ext. 350")}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default RegistrationForm;
